import { createTheme } from '@mui/material/styles';

// Modern Design System Colors - Blue & Professional
export const colors = {
  primary: {
    main: '#0f67ea', // New Primary Blue
    light: '#4b91f1', // Lighter Blue
    dark: '#0a47a3', // Darker Blue
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#0ea5e9', // Sky Blue - Complementary to the primary blue
    light: '#38bdf8',
    dark: '#0284c7',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F8FAFC', // Very light slate/gray - Clean background
    paper: '#FFFFFF',
    sidebar: '#FFFFFF',
    inputDefault: '#F2F5F5', // Light slate for input fields
  },
  text: {
    primary: '#0F172A', // Slate 900 - High contrast
    secondary: '#64748B', // Slate 500 - Subtle
    tertiary: 'rgba(83, 83, 82, 0.05)', // Slate 400 - Muted
    disabled: '#94A3B8',
    dark: '#000',
  },
  border: {
    main: '#E2E8F0', // Slate 200 - Subtle borders
    dark: '#B0BEC5', // Slate 600 - Darker border
  },
  action: {
    hover: '#eff6ff', // Blue 50
    selected: '#dbeafe', // Blue 100
    edit: '#bfdbfe', // Blue 200
    row: '#f8f9fa',
    disabled: 'rgba(241, 245, 249, 0.78)',
    error: '#fef8f8ff',
  },
  error: {
    main: '#EF4444', // Red 500
    light: '#FCA5A5',
    dark: '#B91C1C',
  },
  warning: {
    main: '#F59E0B', // Amber 500
    light: '#FCD34D',
    dark: '#B45309',
  },
  success: {
    main: '#10B981', // Emerald 500 - Semantic success color (kept for standard UI feedback)
    light: '#34D399',
    dark: '#047857',
    completed: '##66FF00',
  },
  info: {
    main: '#3B82F6', // Blue 500
    light: '#60A5FA',
    dark: '#1D4ED8',
  },
  chart: {
    primary: '#0f67ea',
    secondary: '#0ea5e9',
    tertiary: '#6366f1', // Indigo
    quaternary: '#8b5cf6', // Violet
    background: '#FFFFFF',
    up: '#5cb85c', // Green 500
  },
};

export const modernTheme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: colors.text,
    divider: colors.border.main,
    action: {
      hover: colors.action.hover,
      selected: colors.action.hover, // Use same color as hover
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14, // Base font size set to 14px
    // Main headings / Page titles - 32px with primary color
    h1: {
      fontSize: '2rem', // 32px
      fontWeight: 700,
      color: colors.primary.main, // Primary theme color
      letterSpacing: '-0.025em',
    },
    // Subheadings / Section titles - 24px with secondary color
    h2: {
      fontSize: '1.5rem', // 24px
      fontWeight: 600,
      color: colors.text.secondary, // Secondary/low-emphasis color
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.25rem', // 20px
      fontWeight: 600,
      color: colors.text.secondary,
      letterSpacing: '-0.025em',
    },
    h4: {
      fontSize: '1.125rem', // 18px
      fontWeight: 600,
      color: colors.text.secondary,
    },
    h5: {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      color: colors.text.secondary,
    },
    h6: {
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      color: colors.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    // Body text - 14px
    body1: {
      fontSize: '0.875rem', // 14px - Global body text
      lineHeight: 1.6,
      color: colors.text.primary,
    },
    // Small/help/secondary text - 12px
    body2: {
      fontSize: '0.75rem', // 12px - Small/help/secondary text
      lineHeight: 1.5,
      color: colors.text.secondary,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.875rem', // 14px for buttons
    },
    // Helper text styles
    caption: {
      fontSize: '0.75rem', // 12px - Helper/descriptive text
      color: colors.text.secondary,
    },
    subtitle1: {
      fontSize: '0.875rem', // 14px
      color: colors.text.secondary,
    },
    subtitle2: {
      fontSize: '0.75rem', // 12px
      color: colors.text.secondary,
    },
  },
  shape: {
    borderRadius: 8, // Slightly more rounded for modern feel
  },
  shadows: [
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
  ],
  components: {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: 'none',
          fontSize: '0.875rem', // 14px
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            backgroundColor: colors.primary.dark,
          },
        },
        containedPrimary: {
          // Action buttons (Save, Update, Done, Add, Create, Submit) use primary color
          backgroundColor: colors.primary.main,
          color: colors.primary.contrastText,
          '&:hover': {
            backgroundColor: colors.primary.dark,
          },
        },
        outlined: {
          borderColor: colors.border.main,
          color: colors.text.primary,
          '&:hover': {
            backgroundColor: colors.action.hover,
            borderColor: colors.text.primary,
          },
        },
        // Cancel buttons should NOT use primary color
        text: {
          color: colors.text.secondary,
          '&:hover': {
            backgroundColor: colors.action.hover,
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
          border: `1px solid ${colors.border.main}`,
        },
        elevation0: {
          boxShadow: 'none',
          border: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
          border: `1px solid ${colors.border.main}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${colors.border.main}`,
          backgroundColor: colors.background.sidebar,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          fontSize: '0.875rem', // 14px
          '&.Mui-selected': {
            backgroundColor: colors.action.hover,
            color: colors.primary.main,
            '&:hover': {
              backgroundColor: colors.action.hover,
            },
            '& .MuiListItemIcon-root': {
              color: colors.primary.main,
            },
          },
          '&:hover': {
            backgroundColor: colors.action.hover,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          color: colors.text.secondary,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '4px !important',
          overflow: 'hidden',
          border: `1px solid ${colors.border.main}`,
          fontSize: '0.875rem !important', // 14px - Force for container
          '& *': {
            fontSize: '0.875rem !important', // 14px - Force for all children
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem !important', // 14px - Force for table
          '& *': {
            fontSize: '0.875rem !important', // 14px - Force for all children
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem !important', // 14px - Force for table head
          '& *': {
            fontSize: '0.875rem !important', // 14px - Force for all children
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem !important', // 14px - Force for table body
          '& *': {
            fontSize: '0.875rem !important', // 14px - Force for all children
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem !important', // 14px - Force for rows
          '&:last-child td, &:last-child th': {
            borderBottom: 'none !important',
          },
          '& *': {
            fontSize: '0.875rem !important', // 14px - Force for all children
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.border.main}`,
          padding: '8px 16px',
          fontSize: '0.875rem !important', // 14px - Forced for all table cells
        },
        head: {
          fontWeight: 600,
          backgroundColor: colors.background.default,
          color: colors.text.secondary, // Secondary/low-emphasis color for table headers
          fontSize: '0.875rem !important', // 14px - Forced for headers
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          padding: '10px 16px',
          borderBottom: `1px solid ${colors.border.main} !important`,
        },
        body: {
          fontSize: '0.875rem !important', // 14px - Forced for body cells
          color: colors.text.primary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        label: {
          fontSize: '0.75rem',
        },
        colorPrimary: {
          backgroundColor: colors.primary.light,
          color: colors.primary.contrastText,
        },
        colorSecondary: {
          backgroundColor: colors.secondary.light,
          color: colors.secondary.contrastText,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            fontSize: '0.875rem', // 14px - Body text
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23)', // Keep default border color on hover
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main, // Only change on focus
              borderWidth: '1px',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem', // 14px
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.75rem', // 12px - Helper text
            color: colors.text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px - Body text
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)', // Keep default border color on hover
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main, // Only change on focus
            borderWidth: '1px',
          },
        },
        notchedOutline: {
          borderColor: 'rgba(0, 0, 0, 0.23)',
        },
        input: {
          fontSize: '0.875rem', // 14px
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px - Body text
          '&:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)', // Keep default border color on hover
          },
        },
        input: {
          fontSize: '0.875rem', // 14px
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          fontSize: '0.875rem', // 14px - Body text
        },
        select: {
          fontSize: '0.875rem', // 14px
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        outlined: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)', // Keep default border color on hover
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main, // Only change on focus
            borderWidth: '1px',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontSize: '0.875rem', // 14px
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23)', // Keep default border color on hover
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main, // Only change on focus
              borderWidth: '1px',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem', // 14px
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.75rem', // 12px - Helper text
            color: colors.text.secondary,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '2px',
          marginTop: '0px',
        },
        list: {
          padding: '0px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: '0px !important',
          marginTop: '3px',

          paddingTop: '6px',
          paddingBottom: '6px',
          paddingLeft: '8px',
          paddingRight: '8px',
          minHeight: '32px',
          fontSize: '0.875rem', // 14px - Body text

          '&:hover': {
            backgroundColor: colors.action.hover,
            borderRadius: '0px !important',
          },
          '&.Mui-selected': {
            backgroundColor: colors.action.hover,
            borderRadius: '0px !important',
            '&:hover': {
              backgroundColor: colors.action.hover,
              borderRadius: '0px !important',
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontSize: '0.875rem', // 14px
          },
        },
        input: {
          fontSize: '0.875rem', // 14px - Body text
        },
        option: {
          fontSize: '0.875rem', // 14px
          paddingLeft: '8px !important',
          paddingRight: '8px !important',
          paddingTop: '6px !important',
          paddingBottom: '6px !important',
          marginTop: '3px !important',

          minHeight: '32px',
          borderRadius: '0px !important',
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: `${colors.action.hover} !important`,
            borderRadius: '0px !important',
          },
          "&[aria-selected='true']": {
            backgroundColor: `${colors.action.hover} !important`,
            borderRadius: '0px !important',
          },
          '&.Mui-focused': {
            backgroundColor: `${colors.action.hover} !important`,
            borderRadius: '0px !important',
          },
        },
        listbox: {
          padding: '0px',
          backgroundColor: '#ffffff',
        },
        paper: {
          borderRadius: '2px',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: '2px',
          boxShadow: 'none',
          border: `1px solid ${colors.border.main}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          boxShadow: 'none',
          border: `1px solid ${colors.border.main}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.5rem', // 24px - Subheading
          fontWeight: 600,
          color: colors.text.secondary,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px - Body text
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px - Body text
          color: colors.text.primary,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px
        },
        content: {
          fontSize: '0.875rem', // 14px
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem', // 12px - Small text
          backgroundColor: colors.text.primary,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px
        },
        message: {
          fontSize: '0.875rem', // 14px
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px
          color: colors.text.secondary,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px
          color: colors.text.secondary,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem', // 12px - Helper text
          color: colors.text.secondary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.875rem', // 14px
        },
        secondary: {
          fontSize: '0.75rem', // 12px
          color: colors.text.secondary,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px
        },
        li: {
          fontSize: '0.875rem', // 14px
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px
          textTransform: 'none',
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // 14px
        },
      },
    },
  },
});

export default modernTheme;
